import { createTimestampedFileName, downloadTextFile, readFileAsText } from "../core/utils/file";
import { requireElement, setStatus } from "../core/utils/dom";
import { CoordinateExtractor } from "../features/coordinates/CoordinateExtractor";
import { GeoJsonBounds } from "../features/geojson/GeoJsonBounds";
import { GeoJsonParser } from "../features/geojson/GeoJsonParser";
import { ChartModelParser } from "../features/charts/ChartModelParser";
import type { GeoJsonDocument, GeoJsonFeatureCollection } from "../core/types/geojson";
import type { Page } from "./Page";

export class ToolsPage implements Page {
  private latestFeatureNames: string[] = [];

  render(): HTMLElement {
    const page = document.createElement("main");
    page.className = "page";
    page.innerHTML = `
      <section class="card grid-2">
        <div>
          <h1>GeoJSON Point Builder</h1>
          <div class="field"><label for="point-lat">Latitude</label><input id="point-lat" type="number" step="any" value="37.7397" /></div>
          <div class="field"><label for="point-lng">Longitude</label><input id="point-lng" type="number" step="any" value="-121.4252" /></div>
          <div class="field"><label for="property-name">Property name</label><input id="property-name" type="text" value="date" /></div>
          <div class="field"><label for="property-value">Property value</label><input id="property-value" type="text" value="20190509" /></div>
          <div class="button-row">
            <button id="create-point" type="button">Create GeoJSON</button>
            <button id="download-point" type="button">Download</button>
          </div>
        </div>
        <div>
          <h2>Output</h2>
          <textarea id="point-output" class="code-area code-area--large" spellcheck="false"></textarea>
        </div>
      </section>

      <section class="card grid-2">
        <div>
          <h1>Coordinate Extractor</h1>
          <p class="muted">Paste lines in <code>lng,lat</code> format. This returns <code>{ lat, lng }</code> pairs that are easy to paste into map code or documentation.</p>
          <textarea id="coord-input" class="code-area" spellcheck="false">34.26800919,31.22360611\n34.26234055,31.23974609</textarea>
          <button id="parse-coords" type="button">Parse Coordinates</button>
        </div>
        <div>
          <h2>Output</h2>
          <div id="tools-status" class="status status--info">Ready.</div>
          <textarea id="coord-output" class="code-area" spellcheck="false"></textarea>
        </div>
      </section>

      <section class="card grid-2">
        <div>
          <h1>GeoJSON Research Inspector</h1>
          <p class="muted">Use this before sending a file to the Viewer or Drawer. It summarizes feature counts, geometry types, property keys, names used for chart matching, and bounding box.</p>
          <div class="field"><label for="research-geojson-file">Import GeoJSON for analysis</label><input id="research-geojson-file" type="file" accept=".geojson,.json,application/geo+json,application/json" /></div>
          <textarea id="research-geojson-input" class="code-area" spellcheck="false" placeholder="Or paste GeoJSON here"></textarea>
          <div class="button-row">
            <button id="analyze-geojson" type="button">Analyze GeoJSON</button>
            <button id="download-feature-names" type="button">Download Feature Names CSV</button>
            <button id="create-chart-template" type="button">Create Chart Template From Names</button>
          </div>
        </div>
        <div>
          <h2>Research output</h2>
          <textarea id="research-output" class="code-area code-area--large" spellcheck="false"></textarea>
        </div>
      </section>
    `;
    queueMicrotask(() => this.bind(page));
    return page;
  }

  private bind(page: HTMLElement): void {
    const pointOutput = requireElement<HTMLTextAreaElement>("#point-output", page);
    const createPoint = () => {
      const lat = Number(requireElement<HTMLInputElement>("#point-lat", page).value);
      const lng = Number(requireElement<HTMLInputElement>("#point-lng", page).value);
      const key = requireElement<HTMLInputElement>("#property-name", page).value.trim();
      const value = requireElement<HTMLInputElement>("#property-value", page).value;
      const properties = key ? { [key]: value } : {};
      pointOutput.value = JSON.stringify(CoordinateExtractor.createPointFeature(lat, lng, properties), null, 2);
    };

    requireElement<HTMLButtonElement>("#create-point", page).addEventListener("click", createPoint);
    requireElement<HTMLButtonElement>("#download-point", page).addEventListener("click", () => {
      if (!pointOutput.value) createPoint();
      downloadTextFile(createTimestampedFileName("point", "geojson"), pointOutput.value);
    });

    requireElement<HTMLButtonElement>("#parse-coords", page).addEventListener("click", () => {
      const status = requireElement<HTMLDivElement>("#tools-status", page);
      try {
        const input = requireElement<HTMLTextAreaElement>("#coord-input", page).value;
        const points = CoordinateExtractor.parseLngLatLines(input);
        requireElement<HTMLTextAreaElement>("#coord-output", page).value = CoordinateExtractor.formatLatLngPairs(points);
        setStatus(status, `Parsed ${points.length} coordinate pair(s).`, "success");
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Could not parse coordinates.", "error", 0);
      }
    });

    requireElement<HTMLInputElement>("#research-geojson-file", page).addEventListener("change", async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        requireElement<HTMLTextAreaElement>("#research-geojson-input", page).value = await readFileAsText(file);
        this.analyzeResearchGeoJson(page);
      } catch (error) {
        setStatus(requireElement<HTMLDivElement>("#tools-status", page), error instanceof Error ? error.message : "Could not read GeoJSON file.", "error", 0);
      }
    });

    requireElement<HTMLButtonElement>("#analyze-geojson", page).addEventListener("click", () => this.analyzeResearchGeoJson(page));
    requireElement<HTMLButtonElement>("#download-feature-names", page).addEventListener("click", () => {
      if (!this.latestFeatureNames.length) this.analyzeResearchGeoJson(page);
      const csv = ["feature_name", ...this.latestFeatureNames.map((name) => JSON.stringify(name))].join("\n");
      downloadTextFile(createTimestampedFileName("feature_names", "csv"), csv);
    });
    requireElement<HTMLButtonElement>("#create-chart-template", page).addEventListener("click", () => {
      if (!this.latestFeatureNames.length) this.analyzeResearchGeoJson(page);
      const model = ChartModelParser.createTemplate();
      model.legend_title = "Research categories";
      model.legends = [{ name: "Value", color: "#2563eb" }];
      model.unit = "";
      model.chart_caption = "";
      model.chart_max_value = "";
      model.charts = this.latestFeatureNames.map((name) => ({
        country_name: name,
        unit: "",
        chart_caption: `${name} chart`,
        chart_max_value: 100,
        properties: [{ name: "Value", value: 0 }]
      }));
      requireElement<HTMLTextAreaElement>("#research-output", page).value = ChartModelParser.stringify(model);
      setStatus(requireElement<HTMLDivElement>("#tools-status", page), `Created chart template for ${model.charts.length} feature name(s).`, "success");
    });

    createPoint();
  }

  private analyzeResearchGeoJson(page: HTMLElement): void {
    const status = requireElement<HTMLDivElement>("#tools-status", page);
    try {
      const raw = requireElement<HTMLTextAreaElement>("#research-geojson-input", page).value.trim();
      if (!raw) throw new Error("Paste or import a GeoJSON document first.");
      const document = JSON.parse(raw) as GeoJsonDocument;
      const collection = GeoJsonParser.normalizeToFeatureCollection(document);
      const summary = this.buildResearchSummary(collection, document);
      this.latestFeatureNames = summary.featureNames;
      requireElement<HTMLTextAreaElement>("#research-output", page).value = JSON.stringify(summary, null, 2);
      setStatus(status, `Analyzed ${collection.features.length} feature(s).`, "success");
    } catch (error) {
      setStatus(status, error instanceof Error ? error.message : "Could not analyze GeoJSON.", "error", 0);
    }
  }

  private buildResearchSummary(collection: GeoJsonFeatureCollection, document: GeoJsonDocument) {
    const geometryCounts: Record<string, number> = {};
    const propertyKeys = new Set<string>();
    const unnamedFeatureIndexes: number[] = [];
    const featureNames: string[] = [];

    collection.features.forEach((feature, index) => {
      const type = feature.geometry?.type ?? "null";
      geometryCounts[type] = (geometryCounts[type] ?? 0) + 1;
      Object.keys(feature.properties ?? {}).forEach((key) => propertyKeys.add(key));
      const name = this.extractFeatureName(feature.properties ?? {});
      if (name) featureNames.push(name);
      else unnamedFeatureIndexes.push(index + 1);
    });

    return {
      featureCount: collection.features.length,
      geometryCounts,
      propertyKeys: Array.from(propertyKeys).sort(),
      featureNames,
      unnamedFeatureIndexes,
      bounds: GeoJsonBounds.fromDocument(document),
      viewportMessage: GeoJsonBounds.viewportNotice(document).trim(),
      chartMatchingField: "GeoJSON feature.properties.name must match chart charts[].country_name."
    };
  }

  private extractFeatureName(properties: Record<string, unknown>): string {
    for (const key of ["name", "Name", "NAME", "country_name", "countryName", "title", "label"]) {
      const value = properties[key];
      if (typeof value === "string" && value.trim()) return value.trim();
      if (typeof value === "number") return String(value);
    }
    return "";
  }
}
